import { useState, useEffect } from "react";
import axios from "axios";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { API, ax, errorText } from "./common";

// Edits which active warehouses an internal user works at, and with which capability.
export default function WarehouseAssignmentsDialog({ user, onClose, showMsg }) {
  const { t } = useLang();
  const [warehouses, setWarehouses] = useState([]);
  const [initial, setInitial] = useState({});
  const [selected, setSelected] = useState({});

  useEffect(() => {
    if (!user) return;
    Promise.all([
      axios.get(`${API}/warehouses?active=true`, ax),
      axios.get(`${API}/users/${user.id}/warehouse-assignments`, ax),
    ]).then(([w, a]) => {
      const map = Object.fromEntries(a.data.map(x => [x.warehouse_id, x.capabilities]));
      setWarehouses(w.data);
      setInitial(map);
      setSelected(map);
    }).catch(e => showMsg("error", errorText(e, t("adm.error"))));
  }, [user, showMsg, t]);

  const toggle = (wid, cap, checked) => setSelected(s => {
    const current = s[wid] || [];
    return { ...s, [wid]: checked ? [...current, cap] : current.filter(c => c !== cap) };
  });

  const save = async () => {
    try {
      for (const w of warehouses) {
        const next = selected[w.id] || [];
        const prev = initial[w.id] || [];
        if ([...next].sort().join() === [...prev].sort().join()) continue;
        if (next.length) await axios.put(`${API}/users/${user.id}/warehouse-assignments/${w.id}`, { capabilities: next }, ax);
        else await axios.delete(`${API}/users/${user.id}/warehouse-assignments/${w.id}`, ax);
      }
      showMsg("success", t("adm.us.warehouses_saved"));
      onClose();
    } catch (e) { showMsg("error", errorText(e, t("adm.error"))); }
  };

  return (
    <Dialog open={!!user} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{t("adm.us.warehouses_title")} · {user?.name}</DialogTitle></DialogHeader>
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {warehouses.length === 0 && <p className="text-sm text-slate-400">{t("adm.wh.empty")}</p>}
          {warehouses.map(w => (
            <div key={w.id} className="flex items-center justify-between gap-3 border border-slate-100 rounded-lg p-2.5">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-900 truncate">{w.name}</p>
                <p className="text-xs text-slate-400">{w.country}</p>
              </div>
              <div className="flex gap-3 shrink-0">
                {(w.capabilities || []).map(cap => (
                  <label key={cap} className="flex items-center gap-1.5 text-xs">
                    <Checkbox checked={(selected[w.id] || []).includes(cap)} onCheckedChange={v => toggle(w.id, cap, v === true)} />
                    {t(`adm.cap.${cap}`)}
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={save} className="bg-blue-600 hover:bg-blue-700">{t("common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
